import AddToCartButton from "~/components/product/AddToCartButton"
import clsx from "~/sdk/clx"
import { useSignal } from "~/sdk/signal"
import { ImageWidget } from "~/types/widgets"
import type React from "react"
import { useRef, useState } from "react"
import type { ToAddToCart } from "~/sdk/ga4/events/add_to_cart"
interface Item { id: string; seller: string; quantity: number }
import { useSubscription } from "~/sdk/useSubscription"
import Button from "~/components/ui/Button"
export interface Props {
    layout?: 'ProductPage' | 'ProductCard' | 'Countdown-home'
    product: Item
    icon?: ImageWidget
    iconWidth?: number
    iconHeight?: number
    isMobile: boolean
    isPDP?: boolean
    inventoryLevel: number
    analytics?: ToAddToCart
    disabledBuyMonthlySubscription?: boolean
}

export default function AddToCartWithQuantitySelector({
    layout = 'ProductCard',
    product,
    icon,
    iconWidth,
    iconHeight,
    isMobile,
    inventoryLevel,
    analytics,
    disabledBuyMonthlySubscription,
}: Props) {
    const { verifyIfSkuIsSubscription } = useSubscription()
    const skuIsSubscription = verifyIfSkuIsSubscription(product.id)

    const id = `${product.id}-quantity-input`
    const quantity = useSignal(product.quantity ?? 1)
    // What the user is currently typing. `null` = not typing, show `quantity`.
    // Needed because a controlled number input can't hold the intermediate
    // empty/partial states ("", "1") while it's being edited.
    const [draft, setDraft] = useState<string | null>(null)
    const stockAlertRef = useRef<HTMLDivElement | null>(null)

    // Shelf products go through @decocms/apps-vtex/utils/transform.toLeanOffer,
    // which hardcodes inventoryLevel.value = 0 to slim the payload. Treat 0 as
    // "unknown/unlimited" so the + button stays usable on shelves; PDP receives
    // the real value from the full offer transform.
    const stockCap = inventoryLevel > 0 ? inventoryLevel : Number.MAX_SAFE_INTEGER

    const decrement = () => {
        setDraft(null)
        if (quantity.value > 1) {
            quantity.value -= 1
        }
    }

    const increment = () => {
        setDraft(null)
        if (quantity.value < stockCap) {
            quantity.value += 1
        } else {
            stockAlertRef.current?.animate(
                [
                    {
                        opacity: 0,
                        offset: 0,
                    },
                    {
                        opacity: 1,
                        offset: 0.15,
                    },
                    {
                        opacity: 1,
                        offset: 0.85,
                    },
                    {
                        opacity: 0,
                        offset: 1,
                    },
                ],
                {
                    duration: 3000,
                    direction: 'normal',
                    fill: 'forwards',
                },
            )
        }
    }

    const clamp = (value: number) =>
        !Number.isFinite(value) || value < 1 ? 1 : Math.min(Math.trunc(value), stockCap)

    const inputOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, valueAsNumber } = e.currentTarget
        setDraft(value)
        if (value !== '') {
            quantity.value = clamp(valueAsNumber)
        }
    }

    const inputOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setDraft(null)
        quantity.value = clamp(e.currentTarget.valueAsNumber)
    }

    if (disabledBuyMonthlySubscription) {
        return (
            <Button
                className={clsx('w-full', layout === 'ProductPage' && 'max-w-[250px] h-full mt-2')}
                disabled={true}
                data-deco='add-to-cart'
            >
                PRIME ANUAL ATIVO
            </Button>
        )
    }

    return (
        <div
            className={clsx(
                'flex w-full relative gap-1',
                layout === 'ProductPage' && 'mt-2 h-14',
                (layout === 'ProductCard' || layout === 'Countdown-home') &&
                    'h-11 flex-col md:flex-row',
            )}
        >
            {!skuIsSubscription && (
                <>
                    <div
                        ref={stockAlertRef}
                        className='absolute text-center bottom-[calc(100%+4px)] shadow-xl bg-white rounded p-1 font-medium opacity-0 hidden lg:flex'
                    >
                        Limite de estoque atingido!
                    </div>
                    <div
                        className={clsx(
                            'shrink-0 border border-[#cccccc] lg:flex items-center',
                            layout === 'ProductPage' && 'w-[102px] h-full rounded flex',
                            layout === 'ProductCard' && 'w-full lg:w-24 hidden',
                            (layout === 'ProductCard' || layout === 'Countdown-home') &&
                                'h-1/2 md:h-full md:rounded-md',
                            layout === 'Countdown-home' && 'hidden lg:block lg:w-20 xl:w-24',
                        )}
                    >
                        <button
                            type='button'
                            className={clsx(
                                'h-full text-neutral-500 font-bold text-2xl lg:text-xl pb-0.5 cursor-pointer disabled:cursor-not-allowed',
                                (layout === 'ProductCard' || layout === 'Countdown-home') &&
                                    'w-1/5 md:w-[30%]',
                                layout === 'ProductPage' && 'w-[30%]',
                            )}
                            onClick={decrement}
                            disabled={layout === 'ProductPage' && quantity.value === 1}
                        >
                            -
                        </button>
                        <input
                            id={id}
                            className={clsx(
                                'text-center text-neutral-800 [appearance:textfield] h-full bg-transparent max-lg:text-lg',
                                (layout === 'ProductCard' || layout === 'Countdown-home') &&
                                    'w-[60%] md:w-[40%]',
                                layout === 'ProductPage' && 'w-[40%]',
                            )}
                            type='number'
                            inputMode='numeric'
                            pattern='[0-9]*'
                            max={stockCap === Number.MAX_SAFE_INTEGER ? undefined : stockCap}
                            min={1}
                            value={draft ?? quantity.value}
                            onChange={inputOnChange}
                            onBlur={inputOnBlur}
                            maxLength={3}
                            size={3}
                            aria-label='Quantidade'
                        />
                        <button
                            type='button'
                            className={clsx(
                                'h-full text-neutral-500 font-bold text-2xl lg:text-xl pb-0.5 cursor-pointer disabled:cursor-not-allowed',
                                (layout === 'ProductCard' || layout === 'Countdown-home') &&
                                    'w-1/5 md:w-[30%] md:mr-1',
                                layout === 'ProductPage' && 'w-[30%]',
                            )}
                            onClick={increment}
                            disabled={layout === 'ProductPage' && inventoryLevel > 0 && quantity.value === inventoryLevel}
                        >
                            +
                        </button>
                    </div>
                </>
            )}

            <AddToCartButton
                className={clsx(
                    'min-h-full shrink bg-red-wine border-0 text-white lg:hover:bg-[#361318] font-bold rounded text-base focus:!bg-salmon-600',
                    layout === 'ProductPage' && 'w-full max-w-[230px] h-full',
                    (layout === 'ProductCard' || layout === 'Countdown-home') &&
                        `w-full ${isMobile ? 'h-full' : 'h-1/2'} md:h-full`,
                    layout === 'Countdown-home' && 'px-0',
                )}
                products={[{ ...product, quantity: quantity.value }]}
                icon={icon}
                iconWidth={iconWidth}
                iconHeight={iconHeight}
                analytics={analytics &&
                    {
                        ...analytics,
                        items: analytics.items.map((item) => ({
                            ...item,
                            extended: {
                                ...item.extended,
                                quantity: quantity.value,
                            },
                        })),
                    }}
            />
        </div>
    )
}
